import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/select";
import type { ImportOriginRule } from "@/types/data-import.types";
import type { LineOption } from "./ColumnTargetRow";

interface OriginRulesFieldProps {
  rules: ImportOriginRule[];
  /** Linhas ligadas DIRETAMENTE na cadeia, já recortadas às que este arquivo registra. */
  pairs: Array<{ upstreamLineId: string; downstreamLineId: string }>;
  lines: LineOption[];
  onChange: (rules: ImportOriginRule[]) => void;
}

const chave = (r: { upstreamLineId: string; downstreamLineId: string }) =>
  `${r.upstreamLineId}|${r.downstreamLineId}`;

/**
 * Spec 084 (FR-024) — a LIGAÇÃO entre as etapas descritas pelo arquivo.
 *
 * Declará-la vale como confirmar a origem de cada batelada importada (FR-008a): ninguém voltaria
 * para confirmar um ano de histórico dia a dia. Por isso a oferta é fechada — só pares que já
 * existem na cadeia e cujas duas linhas este arquivo registra —, e não um par de listas livres: uma
 * ligação inventada aqui atravessaria o arquivo inteiro antes de alguém perceber.
 */
export function OriginRulesField({ rules, pairs, lines, onChange }: OriginRulesFieldProps) {
  const nome = (id: string) => lines.find((l) => l.id === id)?.name ?? id;
  const disponiveis = pairs.filter((p) => !rules.some((r) => chave(r) === chave(p)));

  if (pairs.length === 0) {
    return (
      <p className="text-[12px] text-text-secondary">
        Nenhuma etapa deste arquivo alimenta outra na cadeia. Ligue as linhas no detalhe da linha
        para que a importação possa associar a origem das bateladas.
      </p>
    );
  }

  return (
    <div className="space-y-2">
      <p className="text-[12px] text-text-secondary">
        Em cada linha do arquivo, a etapa de baixo consome o que a de cima produziu. Declarar isto
        associa a origem de todas as bateladas importadas de uma vez.
      </p>
      {rules.length === 0 ? (
        <p className="text-[12px] text-text-tertiary">
          Nenhuma ligação declarada: as bateladas entram sem origem.
        </p>
      ) : (
        <ul className="space-y-1">
          {rules.map((r) => (
            <li key={chave(r)} className="flex items-center gap-2 text-[13px]">
              <span className="min-w-0 flex-1 truncate text-text-primary">
                {nome(r.upstreamLineId)} → {nome(r.downstreamLineId)}
              </span>
              <Button
                variant="outline"
                size="sm"
                aria-label={`Remover a ligação ${nome(r.upstreamLineId)} para ${nome(r.downstreamLineId)}`}
                onClick={() => onChange(rules.filter((x) => chave(x) !== chave(r)))}
              >
                <Trash2 size={14} />
              </Button>
            </li>
          ))}
        </ul>
      )}
      {disponiveis.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          <Select
            aria-label="Ligação a acrescentar"
            value=""
            onChange={(e) => {
              const escolhido = disponiveis.find((p) => chave(p) === e.target.value);
              if (escolhido) onChange([...rules, escolhido]);
            }}
          >
            <option value="">Escolha uma ligação</option>
            {disponiveis.map((p) => (
              <option key={chave(p)} value={chave(p)}>
                {nome(p.upstreamLineId)} → {nome(p.downstreamLineId)}
              </option>
            ))}
          </Select>
          <Plus size={14} aria-hidden className="text-text-tertiary" />
        </div>
      ) : null}
    </div>
  );
}
